/**
 * Task Templates Database Operations
 *
 * Provides CRUD operations for task templates that allow
 * users to create reusable task configurations.
 */

import { supabase } from '@/lib/supabaseClient';
import { withRetry } from '@/utils/retry';
import { handleError } from '@/utils/errorHandler';
import { handleNotFoundError } from './helpers';

// ============================================
// TYPES
// ============================================

export interface SubtaskTemplate {
  name: string;
  assignees?: string[];
  estimatedTime?: number;
}

export interface ChecklistTemplate {
  name: string;
  items: Array<{
    name: string;
    checked?: boolean;
  }>;
}

export interface TaskTemplateData {
  content?: string;
  priority?: string | null;
  status?: string;
  tags?: string[];
  assignees?: string[];
  timeEstimated?: number | null;
  customFields?: Record<string, unknown> | null;
}

export interface TaskTemplate {
  id: string;
  spaceId: string;
  name: string;
  description: string | null;
  templateData: TaskTemplateData;
  subtaskTemplates: SubtaskTemplate[];
  checklistTemplates: Record<string, ChecklistTemplate>;
  tags: string[];
  defaultAssignees: string[];
  defaultPriority: string | null;
  defaultStatus: string;
  estimatedTime: number | null;
  isGlobal: boolean;
  usageCount: number;
  categoryId: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TemplateCategory {
  id: string;
  spaceId: string | null;
  name: string;
  description: string | null;
  color: string;
  icon: string | null;
  orderIndex: number;
  isDefault: boolean;
  createdAt: string;
}

export interface CreateTemplateInput {
  spaceId: string;
  name: string;
  description?: string;
  templateData?: TaskTemplateData;
  subtaskTemplates?: SubtaskTemplate[];
  checklistTemplates?: Record<string, ChecklistTemplate>;
  tags?: string[];
  defaultAssignees?: string[];
  defaultPriority?: string;
  defaultStatus?: string;
  estimatedTime?: number;
  isGlobal?: boolean;
  categoryId?: string;
  createdBy?: string;
}

export interface UpdateTemplateInput {
  name?: string;
  description?: string;
  templateData?: TaskTemplateData;
  subtaskTemplates?: SubtaskTemplate[];
  checklistTemplates?: Record<string, ChecklistTemplate>;
  tags?: string[];
  defaultAssignees?: string[];
  defaultPriority?: string | null;
  defaultStatus?: string;
  estimatedTime?: number | null;
  isGlobal?: boolean;
  categoryId?: string | null;
}

// ============================================
// CONVERSION HELPERS
// ============================================

function convertTemplateFromSupabase(row: Record<string, unknown>): TaskTemplate {
  return {
    id: row.id as string,
    spaceId: row.space_id as string,
    name: row.name as string,
    description: (row.description as string) || null,
    templateData: (row.template_data as TaskTemplateData) || {},
    subtaskTemplates: (row.subtask_templates as SubtaskTemplate[]) || [],
    checklistTemplates: (row.checklist_templates as Record<string, ChecklistTemplate>) || {},
    tags: (row.tags as string[]) || [],
    defaultAssignees: (row.default_assignees as string[]) || [],
    defaultPriority: (row.default_priority as string) || null,
    defaultStatus: (row.default_status as string) || 'to do',
    estimatedTime: (row.estimated_time as number) || null,
    isGlobal: (row.is_global as boolean) || false,
    usageCount: (row.usage_count as number) || 0,
    categoryId: (row.category_id as string) || null,
    createdBy: (row.created_by as string) || null,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

function convertCategoryFromSupabase(row: Record<string, unknown>): TemplateCategory {
  return {
    id: row.id as string,
    spaceId: (row.space_id as string) || null,
    name: row.name as string,
    description: (row.description as string) || null,
    color: (row.color as string) || '#6366f1',
    icon: (row.icon as string) || null,
    orderIndex: (row.order_index as number) || 0,
    isDefault: (row.is_default as boolean) || false,
    createdAt: row.created_at as string,
  };
}

// ============================================
// TEMPLATE CRUD OPERATIONS
// ============================================

/**
 * Create a new task template
 */
export async function createTemplate(input: CreateTemplateInput): Promise<TaskTemplate> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('task_templates')
      .insert({
      space_id: input.spaceId,
      name: input.name,
      description: input.description || null,
      template_data: input.templateData || {},
      subtask_templates: input.subtaskTemplates || [],
      checklist_templates: input.checklistTemplates || {},
      tags: input.tags || [],
      default_assignees: input.defaultAssignees || [],
      default_priority: input.defaultPriority || null,
      default_status: input.defaultStatus || 'to do',
      estimated_time: input.estimatedTime || null,
      is_global: input.isGlobal || false,
      category_id: input.categoryId || null,
      created_by: input.createdBy || null,
    })
    .select()
    .single()
  );

  if (error) throw error;
  return convertTemplateFromSupabase(data);
}

/**
 * Get a template by ID
 */
export async function getTemplate(templateId: string): Promise<TaskTemplate | null> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('task_templates')
      .select('*')
      .eq('id', templateId)
      .single()
  );

  if (error) return handleNotFoundError(error, null);
  return convertTemplateFromSupabase(data);
}

/**
 * Get all templates for a space
 */
export async function getTemplatesBySpace(spaceId: string): Promise<TaskTemplate[]> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('task_templates')
      .select('*')
      .or(`space_id.eq.${spaceId},is_global.eq.true`)
      .order('usage_count', { ascending: false })
      .order('name', { ascending: true })
  );

  if (error) throw error;
  return (data || []).map(convertTemplateFromSupabase);
}

/**
 * Get all global templates
 */
export async function getGlobalTemplates(): Promise<TaskTemplate[]> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('task_templates')
      .select('*')
      .eq('is_global', true)
      .order('usage_count', { ascending: false })
  );

  if (error) throw error;
  return (data || []).map(convertTemplateFromSupabase);
}

/**
 * Get templates by category
 */
export async function getTemplatesByCategory(categoryId: string): Promise<TaskTemplate[]> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('task_templates')
      .select('*')
      .eq('category_id', categoryId)
      .order('name', { ascending: true })
  );

  if (error) throw error;
  return (data || []).map(convertTemplateFromSupabase);
}

/**
 * Search templates
 */
export async function searchTemplates(
  query: string,
  spaceId?: string
): Promise<TaskTemplate[]> {
  const { data, error } = await withRetry(() => {
    let dbQuery = supabase
      .from('task_templates')
      .select('*')
      .or(`name.ilike.%${query}%,description.ilike.%${query}%`)
      .order('usage_count', { ascending: false })
      .limit(20);

    if (spaceId) {
      dbQuery = dbQuery.or(`space_id.eq.${spaceId},is_global.eq.true`);
    }

    return dbQuery;
  });

  if (error) throw error;
  return (data || []).map(convertTemplateFromSupabase);
}

/**
 * Update a template
 */
export async function updateTemplate(
  templateId: string,
  updates: UpdateTemplateInput
): Promise<TaskTemplate> {
  const updateData: Record<string, unknown> = {};

  if (updates.name !== undefined) updateData.name = updates.name;
  if (updates.description !== undefined) updateData.description = updates.description;
  if (updates.templateData !== undefined) updateData.template_data = updates.templateData;
  if (updates.subtaskTemplates !== undefined) updateData.subtask_templates = updates.subtaskTemplates;
  if (updates.checklistTemplates !== undefined) updateData.checklist_templates = updates.checklistTemplates;
  if (updates.tags !== undefined) updateData.tags = updates.tags;
  if (updates.defaultAssignees !== undefined) updateData.default_assignees = updates.defaultAssignees;
  if (updates.defaultPriority !== undefined) updateData.default_priority = updates.defaultPriority;
  if (updates.defaultStatus !== undefined) updateData.default_status = updates.defaultStatus;
  if (updates.estimatedTime !== undefined) updateData.estimated_time = updates.estimatedTime;
  if (updates.isGlobal !== undefined) updateData.is_global = updates.isGlobal;
  if (updates.categoryId !== undefined) updateData.category_id = updates.categoryId;

  const { data, error } = await withRetry(() =>
    supabase
      .from('task_templates')
      .update(updateData)
      .eq('id', templateId)
      .select()
      .single()
  );

  if (error) throw error;
  return convertTemplateFromSupabase(data);
}

/**
 * Delete a template
 */
export async function deleteTemplate(templateId: string): Promise<void> {
  const { error } = await withRetry(() =>
    supabase
      .from('task_templates')
      .delete()
      .eq('id', templateId)
  );

  if (error) throw error;
}

/**
 * Increment template usage count
 */
export async function incrementTemplateUsage(templateId: string): Promise<void> {
  const { error } = await withRetry(() =>
    supabase.rpc('increment_template_usage', {
      template_id: templateId,
    })
  );

  // If RPC doesn't exist, fall back to fetch-and-increment
  if (error) {
    const template = await getTemplate(templateId);
    if (template) {
      await withRetry(() =>
        supabase
          .from('task_templates')
          .update({ usage_count: template.usageCount + 1 })
          .eq('id', templateId)
      );
    }
  }
}

/**
 * Duplicate a template
 */
export async function duplicateTemplate(
  templateId: string,
  newName?: string,
  targetSpaceId?: string
): Promise<TaskTemplate> {
  const original = await getTemplate(templateId);
  if (!original) {
    throw new Error('Template not found');
  }

  return createTemplate({
    spaceId: targetSpaceId || original.spaceId,
    name: newName || `${original.name} (Copy)`,
    description: original.description || undefined,
    templateData: original.templateData,
    subtaskTemplates: original.subtaskTemplates,
    checklistTemplates: original.checklistTemplates,
    tags: original.tags,
    defaultAssignees: original.defaultAssignees,
    defaultPriority: original.defaultPriority || undefined,
    defaultStatus: original.defaultStatus,
    estimatedTime: original.estimatedTime || undefined,
    isGlobal: false,
    categoryId: original.categoryId || undefined,
  });
}

// ============================================
// CATEGORY CRUD OPERATIONS
// ============================================

/**
 * Create a template category
 */
export async function createCategory(input: {
  spaceId?: string;
  name: string;
  description?: string;
  color?: string;
  icon?: string;
}): Promise<TemplateCategory> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('template_categories')
      .insert({
        space_id: input.spaceId || null,
        name: input.name,
        description: input.description || null,
        color: input.color || '#6366f1',
        icon: input.icon || null,
      })
      .select()
      .single()
  );

  if (error) throw error;
  return convertCategoryFromSupabase(data);
}

/**
 * Get all categories for a space
 */
export async function getCategoriesBySpace(spaceId: string): Promise<TemplateCategory[]> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('template_categories')
      .select('*')
      .or(`space_id.eq.${spaceId},space_id.is.null`)
      .order('order_index', { ascending: true })
      .order('name', { ascending: true })
  );

  if (error) throw error;
  return (data || []).map(convertCategoryFromSupabase);
}

/**
 * Update a category
 */
export async function updateCategory(
  categoryId: string,
  updates: {
    name?: string;
    description?: string;
    color?: string;
    icon?: string;
    orderIndex?: number;
  }
): Promise<TemplateCategory> {
  const updateData: Record<string, unknown> = {};

  if (updates.name !== undefined) updateData.name = updates.name;
  if (updates.description !== undefined) updateData.description = updates.description;
  if (updates.color !== undefined) updateData.color = updates.color;
  if (updates.icon !== undefined) updateData.icon = updates.icon;
  if (updates.orderIndex !== undefined) updateData.order_index = updates.orderIndex;

  const { data, error } = await withRetry(() =>
    supabase
      .from('template_categories')
      .update(updateData)
      .eq('id', categoryId)
      .select()
      .single()
  );

  if (error) throw error;
  return convertCategoryFromSupabase(data);
}

/**
 * Delete a category
 */
export async function deleteCategory(categoryId: string): Promise<void> {
  const { error } = await withRetry(() =>
    supabase
      .from('template_categories')
      .delete()
      .eq('id', categoryId)
  );

  if (error) throw error;
}

// ============================================
// TASK CREATION FROM TEMPLATE
// ============================================

/**
 * Generate task data from a template
 * Returns data ready to be passed to createTask
 */
export function generateTaskFromTemplate(
  template: TaskTemplate,
  overrides?: Partial<TaskTemplateData> & { name?: string; spaceId?: string }
): {
  name: string;
  content: string;
  status: string;
  priority: string | null;
  tags: string[];
  assignees: string[];
  timeEstimated: number | null;
  customFields: Record<string, unknown> | null;
  spaceId?: string;
  templateId: string;
  subtasks: SubtaskTemplate[];
  checklists: Record<string, ChecklistTemplate>;
} {
  const templateData = template.templateData;

  return {
    name: overrides?.name || template.name,
    content: overrides?.content || templateData.content || '',
    status: overrides?.status || template.defaultStatus,
    priority: overrides?.priority !== undefined ? overrides.priority : template.defaultPriority,
    tags: overrides?.tags || template.tags,
    assignees: overrides?.assignees || template.defaultAssignees,
    timeEstimated: overrides?.timeEstimated !== undefined
      ? overrides.timeEstimated
      : template.estimatedTime,
    customFields: overrides?.customFields || templateData.customFields || null,
    spaceId: overrides?.spaceId,
    templateId: template.id,
    subtasks: template.subtaskTemplates,
    checklists: template.checklistTemplates,
  };
}

// ============================================
// BUILT-IN TEMPLATES
// ============================================

export const BUILTIN_TEMPLATES: Array<Omit<CreateTemplateInput, 'spaceId'> & { key: string }> = [
  {
    key: 'bug-report',
    name: 'Bug Report',
    description: 'Template for reporting bugs',
    templateData: {
      content: `## Description
Describe the bug clearly.

## Steps to Reproduce
1. Step one
2. Step two
3. Step three

## Expected Behavior
What should happen?

## Actual Behavior
What actually happens?

## Environment
- OS:
- Browser:
- Version:`,
      priority: 'high',
    },
    tags: ['bug'],
    defaultStatus: 'to do',
    checklistTemplates: {
      verification: {
        name: 'Verification',
        items: [
          { name: 'Bug reproduced' },
          { name: 'Root cause identified' },
          { name: 'Fix implemented' },
          { name: 'Regression test added' },
        ],
      },
    },
  },
  {
    key: 'feature-request',
    name: 'Feature Request',
    description: 'Template for new feature requests',
    templateData: {
      content: `## Overview
Brief description of the feature.

## User Story
As a [type of user], I want [goal] so that [benefit].

## Acceptance Criteria
- [ ] Criterion 1
- [ ] Criterion 2
- [ ] Criterion 3

## Design Notes
Any design considerations or mockups.

## Technical Notes
Implementation details or technical requirements.`,
      priority: 'medium',
    },
    tags: ['feature'],
    defaultStatus: 'to do',
    subtaskTemplates: [
      { name: 'Design review' },
      { name: 'Implementation' },
      { name: 'Testing' },
      { name: 'Documentation' },
    ],
  },
  {
    key: 'meeting-notes',
    name: 'Meeting Notes',
    description: 'Template for capturing meeting notes',
    templateData: {
      content: `## Meeting Details
- **Date:**
- **Time:**
- **Attendees:**

## Agenda
1. Topic 1
2. Topic 2
3. Topic 3

## Discussion Notes


## Action Items


## Next Steps

`,
    },
    tags: ['meeting'],
    defaultStatus: 'to do',
  },
  {
    key: 'sprint-task',
    name: 'Sprint Task',
    description: 'Standard sprint task template',
    templateData: {
      content: `## Task Description


## Technical Approach


## Dependencies
- None

## Testing Plan
- Unit tests
- Integration tests`,
      priority: 'medium',
    },
    defaultStatus: 'to do',
    checklistTemplates: {
      done: {
        name: 'Definition of Done',
        items: [
          { name: 'Code complete' },
          { name: 'Tests written' },
          { name: 'Code reviewed' },
          { name: 'Documentation updated' },
        ],
      },
    },
  },
];

/**
 * Initialize built-in templates for a space
 */
export async function initializeBuiltinTemplates(spaceId: string): Promise<TaskTemplate[]> {
  const templates: TaskTemplate[] = [];

  for (const builtin of BUILTIN_TEMPLATES) {
    try {
      const template = await createTemplate({
        ...builtin,
        spaceId,
        isGlobal: false,
      });
      templates.push(template);
    } catch (err) {
      handleError(err, { level: 'warning', message: `Error creating built-in template ${builtin.key}` });
    }
  }

  return templates;
}
